import { ReactNode } from 'react';

export type StatusType = 'online' | 'offline' | 'warning' | 'pending' | 'error' | 'success';

interface StatusBadgeProps {
  status: StatusType;
  label?: string;
  showDot?: boolean;
  size?: 'sm' | 'md';
  children?: ReactNode;
}

const statusConfig: Record<StatusType, { dot: string; bg: string; text: string }> = {
  online: {
    dot: 'bg-green-500',
    bg: 'bg-green-100',
    text: 'text-green-800',
  },
  offline: {
    dot: 'bg-gray-400',
    bg: 'bg-gray-100',
    text: 'text-gray-800',
  },
  warning: {
    dot: 'bg-yellow-500',
    bg: 'bg-yellow-100',
    text: 'text-yellow-800',
  },
  pending: {
    dot: 'bg-blue-500',
    bg: 'bg-blue-100',
    text: 'text-blue-800',
  },
  error: {
    dot: 'bg-red-500',
    bg: 'bg-red-100',
    text: 'text-red-800',
  },
  success: {
    dot: 'bg-green-500',
    bg: 'bg-green-100',
    text: 'text-green-800',
  },
};

export function StatusBadge({
  status,
  label,
  showDot = true,
  size = 'md',
  children
}: StatusBadgeProps) {
  const config = statusConfig[status];
  const sizeClasses = size === 'sm'
    ? 'px-2 py-0.5 text-xs'
    : 'px-2.5 py-1 text-sm';
  const dotSize = size === 'sm' ? 'w-1.5 h-1.5' : 'w-2 h-2';

  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full font-medium ${config.bg} ${config.text} ${sizeClasses}`}
    >
      {showDot && (
        <span className={`${dotSize} rounded-full ${config.dot}`} aria-hidden="true" />
      )}
      {label || children}
    </span>
  );
}

interface StatusDotProps {
  status: StatusType;
  size?: 'sm' | 'md' | 'lg';
  pulse?: boolean;
}

export function StatusDot({ status, size = 'md', pulse = false }: StatusDotProps) {
  const config = statusConfig[status];
  const sizeClasses = {
    sm: 'w-2 h-2',
    md: 'w-2.5 h-2.5',
    lg: 'w-3 h-3',
  };

  return (
    <span className="relative inline-flex">
      <span
        className={`${sizeClasses[size]} rounded-full ${config.dot}`}
        aria-hidden="true"
      />
      {pulse && (
        <span
          className={`absolute inline-flex h-full w-full animate-ping rounded-full ${config.dot} opacity-75`}
          aria-hidden="true"
        />
      )}
    </span>
  );
}
